import React, { useMemo } from 'react';
import { BackgroundThemeRevision, ColorId } from 'referral-types';
import chroma from 'chroma-js';
import { rgbStringToHex } from '../../../../../utils/colorUtils';
import { useTheme } from '../../../../../context/theme/useTheme';
import { useBodyStyleVersion } from '../../../../../hooks/useBodyStyleVersion';
import { getCssVariableForColorId } from '../../../../../constants';

/*

this takes 2 colors: #DD9933, #330000


generated css:

background-color: #dd9933;
background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Cg %3E%3Cpath fill='%23d38b30' d='M1920 1080V0H120C87.19 63.34 51.17 118.22 0 173.72V1080h1920Z'/%3E%3Cpath fill='%23c97e2e' d='M1920 1080V0H330c-32.86 60.78-67.84 115.34-115.29 166.44C143.27 243.37 69.14 301.79 0 339.78V1080h1920Z'/%3E%3Cpath fill='%23be712c' d='M1920 1080V0H550c-51.34 95.3-112.21 179.19-193.95 250.85C236.83 355.37 108.84 419.36 0 447.31V1080h1920Z'/%3E%3Cpath fill='%23b3652a' d='M1920 0H775c-69.54 131.87-159.5 245.45-278.6 335.11C341.09 452.04 161.77 519.55 0 535.4V1080h1920V0Z'/%3E%3Cpath fill='%23a75928' d='M1920 0h-920c-86.99 169.4-206.09 313.4-364.4 419.24C453.35 541.09 230.22 613.31 0 614.92V1080h1920V0Z'/%3E%3Cpath fill='%239a4d25' d='M1920 0h-700c-103.85 207.84-247.54 382.11-446.89 503.22C557.71 634.09 289.51 705.4 0 690.1V1080h1920V0Z'/%3E%3Cpath fill='%238e4223' d='M1440 0c-121.99 250.64-293.46 453.33-531.74 587.07C637.86 738.84 319.42 793.17 0 762.95V1080h1920V0h-480Z'/%3E%3Cpath fill='%23813720' d='M1658 0c-135.96 286.28-333.97 521.67-617.98 670.79C721.81 837.87 353.75 878.83 0 834.7V1080h1920V0h-262Z'/%3E%3Cpath fill='%23742d1d' d='M1920 1080V0h-62c-152.72 328.91-378.74 593.29-703.62 754.39C799.93 930.16 390.05 963.35 0 908.04V1080h1920Z'/%3E%3Cpath fill='%23672319' d='M1920 1080V230c-166.34 268.55-386.21 474.74-668.34 607.88-346.82 163.67-725.74 193.25-1039.86 169.91A2851.46 2851.46 0 0 1 .01 983.88V1080h1920Z'/%3E%3Cpath fill='%23591915' d='M755 1080h1165V480c-303.27 360.95-708.03 544.47-1165 600Z'/%3E%3Cpath fill='%234c1011' d='M1920 670c-203.11 194.05-441.44 327.61-710 410h710V670Z'/%3E%3Cpath fill='%233f070a' d='M1920 830c-121.56 96.94-263.36 182.07-420 250h420V830Z'/%3E%3Cpath fill='%23300' d='M1920 970c-57.41 39.18-116.63 77.92-176.38 110H1920V970Z'/%3E%3C/g%3E%3C/svg%3E");
background-attachment: fixed;
background-size: cover;

*/

const PlanetaryRingsBackground: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { colorId1, colorId2 } = props.themeRevision || {};
  const { isDarkMode } = useTheme();
  const bodyStyleVersion = useBodyStyleVersion();

  const computedColors = useMemo(() => {
    const colorStyles1 = getCssVariableForColorId(colorId1 || ColorId.Primary2, isDarkMode);
    const colorStyles2 = getCssVariableForColorId(colorId2 || ColorId.Primary4, isDarkMode);
    const rootStyles = getComputedStyle(document.body);
    const c1 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles1).trim() || '221 153 51');
    const c2 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles2).trim() || '51 0 0');

    const colors = [chroma(c1), chroma(c2)];
    const scale = chroma.scale(colors).mode('hsl').colors(14);

    return scale;
  }, [colorId1, colorId2, isDarkMode, bodyStyleVersion]);

  const svgContent = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
      <g>
        <path fill="${computedColors[0]}" d="M1920 1080V0H120C87.19 63.34 51.17 118.22 0 173.72V1080h1920Z"/>
        <path fill="${computedColors[1]}" d="M1920 1080V0H330c-32.86 60.78-67.84 115.34-115.29 166.44C143.27 243.37 69.14 301.79 0 339.78V1080h1920Z"/>
        <path fill="${computedColors[2]}" d="M1920 1080V0H550c-51.34 95.3-112.21 179.19-193.95 250.85C236.83 355.37 108.84 419.36 0 447.31V1080h1920Z"/>
        <path fill="${computedColors[3]}" d="M1920 0H775c-69.54 131.87-159.5 245.45-278.6 335.11C341.09 452.04 161.77 519.55 0 535.4V1080h1920V0Z"/>
        <path fill="${computedColors[4]}" d="M1920 0h-920c-86.99 169.4-206.09 313.4-364.4 419.24C453.35 541.09 230.22 613.31 0 614.92V1080h1920V0Z"/>
        <path fill="${computedColors[5]}" d="M1920 0h-700c-103.85 207.84-247.54 382.11-446.89 503.22C557.71 634.09 289.51 705.4 0 690.1V1080h1920V0Z"/>
        <path fill="${computedColors[6]}" d="M1440 0c-121.99 250.64-293.46 453.33-531.74 587.07C637.86 738.84 319.42 793.17 0 762.95V1080h1920V0h-480Z"/>
        <path fill="${computedColors[7]}" d="M1658 0c-135.96 286.28-333.97 521.67-617.98 670.79C721.81 837.87 353.75 878.83 0 834.7V1080h1920V0h-262Z"/>
        <path fill="${computedColors[8]}" d="M1920 1080V0h-62c-152.72 328.91-378.74 593.29-703.62 754.39C799.93 930.16 390.05 963.35 0 908.04V1080h1920Z"/>
        <path fill="${computedColors[9]}" d="M1920 1080V230c-166.34 268.55-386.21 474.74-668.34 607.88-346.82 163.67-725.74 193.25-1039.86 169.91A2851.46 2851.46 0 0 1 .01 983.88V1080h1920Z"/>
        <path fill="${computedColors[10]}" d="M755 1080h1165V480c-303.27 360.95-708.03 544.47-1165 600Z"/>
        <path fill="${computedColors[11]}" d="M1920 670c-203.11 194.05-441.44 327.61-710 410h710V670Z"/>
        <path fill="${computedColors[12]}" d="M1920 830c-121.56 96.94-263.36 182.07-420 250h420V830Z"/>
        <path fill="${computedColors[13]}" d="M1920 970c-57.41 39.18-116.63 77.92-176.38 110H1920V970Z"/>
      </g>
    </svg>
  `;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: computedColors[0],
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svgContent)}")`,
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  );
};

export default PlanetaryRingsBackground;
